import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import NavBar from '../components/NavBar';
import BackButton from '../components/BackButton';
import { auth } from '../firebase';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { isAdminUserEmail } from '../utils/adminAccess';
import './Renders.css';
import './AdminBackOffice.css';
import { Skeleton, SkeletonTable } from '../components/Skeleton';

interface ProjectUsage {
  project_id: string;
  project_name: string;
  size_bytes: number;
  size_mb: number;
}

interface UserUsage {
  email: string;
  total_bytes: number;
  total_mb: number;
  project_count: number;
  projects: ProjectUsage[];
}

interface UsageResponse {
  status: string;
  users: UserUsage[];
  platform_total_bytes: number;
  platform_total_mb: number;
  storage_free_bytes: number | null;
  storage_free_mb: number | null;
  message?: string;
}

interface ProjectFont {
  name: string;
  filename: string;
  size_bytes: number;
  size_mb: number;
  modified_at: string;
  url: string;
}

interface ProjectFontsResponse {
  status: string;
  fonts: ProjectFont[];
  message?: string;
}

interface ProjectFontRow extends ProjectFont {
  modifiedLabel: string;
}

interface DeleteFontsResponse {
  status: string;
  deleted: string[];
  not_found: string[];
  errors: string[];
  message?: string;
}

interface DeleteProjectResponse {
  status: string;
  deleted?: boolean;
  project_id?: string;
  project_name?: string;
  message?: string;
}

interface DeleteUserResponse {
  status: string;
  email?: string;
  deleted_project_count?: number;
  message?: string;
}

const AdminBackOffice: React.FC = () => {
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [usageData, setUsageData] = useState<UsageResponse | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectFonts, setProjectFonts] = useState<ProjectFont[]>([]);
  const [fontsLoading, setFontsLoading] = useState<boolean>(false);
  const [fontsError, setFontsError] = useState<string>('');
  const [selectedFontNames, setSelectedFontNames] = useState<Set<string>>(new Set());
  const [deletingFonts, setDeletingFonts] = useState<boolean>(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string>('');
  const [deletingUserEmail, setDeletingUserEmail] = useState<string>('');
  const [actionError, setActionError] = useState<string>('');
  const [actionMessage, setActionMessage] = useState<string>('');

  const isAdmin = isAdminUserEmail(currentUserEmail);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserEmail(user?.email || '');
    });
    return () => unsubscribe();
  }, []);

  const loadUsage = useCallback(async (): Promise<void> => {
    if (!isAdmin) {
      setUsageData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/back-office/usage`);
      const payload: UsageResponse = await response.json();
      if (!response.ok || payload.status !== 'OK') {
        throw new Error(payload.message || 'Failed to load back-office usage');
      }
      setUsageData(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load back-office usage');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const users: UserUsage[] = useMemo(() => usageData?.users || [], [usageData]);
  const selectedUser: UserUsage | undefined = useMemo(
    () => users.find((user) => user.email === selectedEmail) || users[0],
    [selectedEmail, users]
  );
  const selectedProject: ProjectUsage | undefined = useMemo(
    () => selectedUser?.projects.find((project) => project.project_id === selectedProjectId),
    [selectedProjectId, selectedUser]
  );
  const projectFontRows: ProjectFontRow[] = useMemo(
    () => projectFonts.map((font) => ({
      ...font,
      modifiedLabel: new Date(font.modified_at).toLocaleString(),
    })),
    [projectFonts]
  );
  const selectedFontCount: number = useMemo(
    () => projectFonts.filter((font) => selectedFontNames.has(font.filename)).length,
    [projectFonts, selectedFontNames]
  );
  const allFontsSelected: boolean = projectFonts.length > 0
    && projectFonts.every((font) => selectedFontNames.has(font.filename));

  const formatTopLevelStorage = (sizeMb: number): string => {
    if (sizeMb > 1024) {
      return `${(sizeMb / 1024).toFixed(1)} GB`;
    }
    return `${sizeMb.toFixed(2)} MB`;
  };

  useEffect(() => {
    if (!selectedUser) {
      setSelectedEmail('');
      return;
    }
    if (selectedEmail !== selectedUser.email) {
      setSelectedEmail(selectedUser.email);
    }
  }, [selectedEmail, selectedUser]);

  useEffect(() => {
    if (!selectedUser || selectedUser.projects.length === 0) {
      setSelectedProjectId('');
      return;
    }

    const hasSelectedProject: boolean = selectedUser.projects.some(
      (project) => project.project_id === selectedProjectId
    );
    if (!hasSelectedProject) {
      setSelectedProjectId(selectedUser.projects[0].project_id);
    }
  }, [selectedProjectId, selectedUser]);

  const loadProjectFonts = useCallback(async (projectId: string): Promise<void> => {
    if (!projectId) {
      setProjectFonts([]);
      setSelectedFontNames(new Set());
      return;
    }

    try {
      setFontsLoading(true);
      setFontsError('');
      const response = await authFetch(`${getApiUrl()}/back-office/projects/${encodeURIComponent(projectId)}/fonts`);
      const payload: ProjectFontsResponse = await response.json();
      if (!response.ok || payload.status !== 'OK') {
        throw new Error(payload.message || 'Failed to load project fonts');
      }
      setProjectFonts(payload.fonts);
      setSelectedFontNames(new Set());
    } catch (err) {
      setProjectFonts([]);
      setSelectedFontNames(new Set());
      setFontsError(err instanceof Error ? err.message : 'Failed to load project fonts');
    } finally {
      setFontsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin || !selectedProjectId) {
      setProjectFonts([]);
      setSelectedFontNames(new Set());
      return;
    }

    loadProjectFonts(selectedProjectId);
  }, [isAdmin, loadProjectFonts, selectedProjectId]);

  const setFontSelection = (filename: string, checked: boolean): void => {
    setSelectedFontNames((previousNames: Set<string>) => {
      const nextNames: Set<string> = new Set(previousNames);
      if (checked) {
        nextNames.add(filename);
      } else {
        nextNames.delete(filename);
      }
      return nextNames;
    });
  };

  const setAllFontSelection = (checked: boolean): void => {
    setSelectedFontNames(checked ? new Set(projectFonts.map((font) => font.filename)) : new Set());
  };

  const deleteFonts = async (fontNames: string[], deleteAll: boolean = false): Promise<void> => {
    if (!selectedProjectId || deletingFonts) {
      return;
    }

    const targetCount: number = deleteAll ? projectFonts.length : fontNames.length;
    if (targetCount === 0) {
      return;
    }

    const projectLabel: string = selectedProject?.project_name || selectedProjectId;
    const confirmMessage: string = deleteAll
      ? `Delete all ${targetCount} font(s) from "${projectLabel}"?`
      : `Delete ${targetCount} selected font(s) from "${projectLabel}"?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingFonts(true);
      setFontsError('');
      const response = await authFetch(
        `${getApiUrl()}/back-office/projects/${encodeURIComponent(selectedProjectId)}/fonts`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fontNames.length > 0 ? { fontNames } : { deleteAll: true }),
        }
      );
      const payload: DeleteFontsResponse = await response.json();
      if (!response.ok || payload.status !== 'OK') {
        throw new Error(payload.message || payload.errors?.join(', ') || 'Failed to delete fonts');
      }
      if (payload.errors.length > 0) {
        setFontsError(payload.errors.join(', '));
      }
      await loadProjectFonts(selectedProjectId);
      await loadUsage();
    } catch (err) {
      setFontsError(err instanceof Error ? err.message : 'Failed to delete fonts');
    } finally {
      setDeletingFonts(false);
    }
  };

  const deleteSelectedFonts = async (): Promise<void> => {
    const selectedNames: string[] = Array.from(selectedFontNames);
    await deleteFonts(selectedNames, allFontsSelected);
  };

  const deleteProject = async (project: ProjectUsage): Promise<void> => {
    if (!project.project_id || deletingProjectId || deletingUserEmail) {
      return;
    }

    const projectLabel: string = project.project_name || project.project_id;
    const confirmMessage: string = `Delete project "${projectLabel}" and all of its files?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingProjectId(project.project_id);
      setActionError('');
      setActionMessage('');
      const response = await authFetch(
        `${getApiUrl()}/back-office/projects/${encodeURIComponent(project.project_id)}`,
        { method: 'DELETE' }
      );
      const payload: DeleteProjectResponse = await response.json();
      if (!response.ok || payload.status !== 'OK') {
        throw new Error(payload.message || 'Failed to delete project');
      }
      if (selectedProjectId === project.project_id) {
        setSelectedProjectId('');
        setProjectFonts([]);
        setSelectedFontNames(new Set());
      }
      setActionMessage(`Deleted project "${projectLabel}".`);
      await loadUsage();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setDeletingProjectId('');
    }
  };

  const deleteUser = async (user: UserUsage): Promise<void> => {
    if (!user.email || deletingUserEmail || deletingProjectId) {
      return;
    }

    const confirmMessage: string = `Delete user "${user.email}" and all ${user.project_count} project(s) associated with this user?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingUserEmail(user.email);
      setActionError('');
      setActionMessage('');
      const response = await authFetch(
        `${getApiUrl()}/back-office/users/${encodeURIComponent(user.email)}`,
        { method: 'DELETE' }
      );
      const payload: DeleteUserResponse = await response.json();
      if (!response.ok || payload.status !== 'OK') {
        throw new Error(payload.message || 'Failed to delete user');
      }
      if (selectedEmail === user.email) {
        setSelectedEmail('');
        setSelectedProjectId('');
        setProjectFonts([]);
        setSelectedFontNames(new Set());
      }
      setActionMessage(`Deleted user "${user.email}" and ${payload.deleted_project_count || 0} project(s).`);
      await loadUsage();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeletingUserEmail('');
    }
  };

  return (
    <>
      <NavBar />
      <div className="admin-page">
        <div className="admin-shell renders-page">
          <BackButton spacing />
          <div className="admin-header">
            <div>
              <Typography variant="h4" className="admin-title">
                Back Office
              </Typography>
              <Typography variant="body1" className="admin-subtitle">
                Disk usage by user and per-project breakdown.
              </Typography>
            </div>
            {isAdmin && (
              <div className="admin-external-links">
                <Button
                  component="a"
                  href="https://console.statsig.com/1mx1oB6Ad4dNXgtoxtY6pT/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Analytics
                </Button>
                <Button
                  component="a"
                  href="https://platform.openai.com/settings/organization/usage"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Chatbot data
                </Button>
              </div>
            )}
          </div>

          {!isAdmin && (
            <Paper className="admin-state-panel" elevation={1}>
              <Alert severity="error">You are signed in but not authorized to access this back office.</Alert>
            </Paper>
          )}

          {isAdmin && loading && (
            <Paper className="admin-state-panel" elevation={1} aria-busy="true" aria-label="Loading usage data" role="status">
              <span className="sr-only">Loading usage data...</span>
              <div className="skeleton-kpi-grid">
                {Array.from({ length: 4 }, (_, index) => (
                  <div className="skeleton-kpi" key={index} aria-hidden="true">
                    <Skeleton width="58%" height={11} />
                    <Skeleton width="42%" height={24} />
                  </div>
                ))}
              </div>
              <div className="admin-grid" aria-hidden="true">
                <Paper className="admin-widget" elevation={1}><SkeletonTable columns={2} rows={6} /></Paper>
                <Paper className="admin-widget" elevation={1}><SkeletonTable columns={3} rows={6} /></Paper>
              </div>
            </Paper>
          )}

          {isAdmin && !loading && error && (
            <Paper className="admin-state-panel" elevation={1}>
              <Alert severity="error">{error}</Alert>
            </Paper>
          )}

          {isAdmin && !loading && !error && usageData && (
            <>
              <div className="admin-kpis">
                <Paper className="admin-kpi" elevation={1}>
                  <Typography variant="overline">Platform used</Typography>
                  <Typography variant="h5">{formatTopLevelStorage(usageData.platform_total_mb)}</Typography>
                </Paper>
                <Paper className="admin-kpi" elevation={1}>
                  <Typography variant="overline">Storage free</Typography>
                  <Typography variant="h5">
                    {typeof usageData.storage_free_mb === 'number'
                      ? formatTopLevelStorage(usageData.storage_free_mb)
                      : 'N/A'}
                  </Typography>
                </Paper>
                <Paper className="admin-kpi" elevation={1}>
                  <Typography variant="overline">Users</Typography>
                  <Typography variant="h5">{users.length}</Typography>
                </Paper>
                <Paper className="admin-kpi" elevation={1}>
                  <Typography variant="overline"># Projects</Typography>
                  <Typography variant="h5">
                    {users.reduce((sum, user) => sum + user.project_count, 0)}
                  </Typography>
                </Paper>
              </div>

              {actionError && (
                <Alert severity="error" className="admin-action-alert">
                  {actionError}
                </Alert>
              )}

              {actionMessage && (
                <Alert severity="success" className="admin-action-alert">
                  {actionMessage}
                </Alert>
              )}

              <div className="admin-grid">
                <Paper className="admin-widget" elevation={1}>
                  <div className="admin-widget-header">
                    <Typography variant="h6">Users by disk usage</Typography>
                    <Typography variant="body2">Sorted descending</Typography>
                  </div>
                  <List className="admin-users-list">
                    {users.map((user) => (
                      <ListItemButton
                        key={user.email}
                        selected={selectedUser?.email === user.email}
                        onClick={() => setSelectedEmail(user.email)}
                      >
                        <ListItemText
                          primary={user.email}
                          secondary={`${user.total_mb.toFixed(2)} MB - ${user.project_count} project(s)`}
                        />
                        <Tooltip title={`Delete ${user.email}`}>
                          <span className="admin-user-delete-action">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={Boolean(deletingUserEmail) || Boolean(deletingProjectId)}
                              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.stopPropagation();
                                deleteUser(user);
                              }}
                              aria-label={`Delete user ${user.email}`}
                            >
                              {deletingUserEmail === user.email ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>

                <Paper className="admin-widget" elevation={1}>
                  <div className="admin-widget-header">
                    <Typography variant="h6">
                      Projects for {selectedUser?.email || 'selected user'}
                    </Typography>
                    <Typography variant="body2">Sorted descending</Typography>
                  </div>
                  {selectedUser && selectedUser.projects.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Project</TableCell>
                          <TableCell align="right">Size (MB)</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedUser.projects.map((project) => (
                          <TableRow
                            key={`${selectedUser.email}-${project.project_id}`}
                            hover
                            selected={selectedProjectId === project.project_id}
                            className="admin-clickable-row"
                            onClick={() => setSelectedProjectId(project.project_id)}
                          >
                            <TableCell>{project.project_name || project.project_id}</TableCell>
                            <TableCell align="right">{project.size_mb.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title={`Delete ${project.project_name || project.project_id}`}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={Boolean(deletingProjectId) || Boolean(deletingUserEmail)}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                      event.stopPropagation();
                                      deleteProject(project);
                                    }}
                                    aria-label={`Delete project ${project.project_name || project.project_id}`}
                                  >
                                    {deletingProjectId === project.project_id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box className="admin-empty">
                      <Typography variant="body2">No projects found for this user.</Typography>
                    </Box>
                  )}
                </Paper>

                <Paper className="admin-widget admin-fonts-widget" elevation={1}>
                  <div className="admin-widget-header admin-fonts-header">
                    <div>
                      <Typography variant="h6">
                        Fonts for {selectedProject?.project_name || selectedProject?.project_id || 'selected project'}
                      </Typography>
                      <Typography variant="body2">
                        {selectedProject ? `${projectFonts.length} font(s) uploaded` : 'Select a project to inspect fonts'}
                      </Typography>
                    </div>
                    <div className="admin-font-actions">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedProject || selectedFontCount === 0 || deletingFonts}
                        onClick={deleteSelectedFonts}
                      >
                        {allFontsSelected ? `Delete All (${selectedFontCount})` : `Delete Selected (${selectedFontCount})`}
                      </Button>
                    </div>
                  </div>

                  {fontsError && (
                    <Alert severity="error" className="admin-fonts-alert">
                      {fontsError}
                    </Alert>
                  )}

                  {fontsLoading ? (
                    <Box aria-busy="true" aria-label="Loading project fonts" role="status">
                      <span className="sr-only">Loading project fonts...</span>
                      <SkeletonTable columns={5} rows={5} />
                    </Box>
                  ) : selectedProject && projectFonts.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <input
                              type="checkbox"
                              className="admin-font-checkbox"
                              checked={allFontsSelected}
                              ref={(element: HTMLInputElement | null) => {
                                if (element) {
                                  element.indeterminate = selectedFontCount > 0 && !allFontsSelected;
                                }
                              }}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAllFontSelection(event.target.checked)}
                              aria-label="Select all project fonts"
                            />
                          </TableCell>
                          <TableCell>Filename</TableCell>
                          <TableCell align="right">Size (MB)</TableCell>
                          <TableCell>Modified</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectFontRows.map((font) => (
                          <TableRow key={`${selectedProject.project_id}-${font.filename}`}>
                            <TableCell padding="checkbox">
                              <input
                                type="checkbox"
                                className="admin-font-checkbox"
                                checked={selectedFontNames.has(font.filename)}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFontSelection(font.filename, event.target.checked)}
                                aria-label={`Select ${font.filename}`}
                              />
                            </TableCell>
                            <TableCell>{font.filename}</TableCell>
                            <TableCell align="right">{font.size_mb.toFixed(2)}</TableCell>
                            <TableCell>{font.modifiedLabel}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                color="error"
                                disabled={deletingFonts}
                                onClick={() => deleteFonts([font.filename])}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box className="admin-empty">
                      <Typography variant="body2">
                        {selectedProject ? 'No fonts uploaded for this project.' : 'Select a project to view fonts.'}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminBackOffice;
